import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "./components/ui/sonner";
import { SettingsProvider } from "./lib/settings";
import { AuthProvider } from "./lib/auth";
import { Header } from "./components/site/Header";
import { Footer } from "./components/site/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Divisions from "./pages/Divisions";
import Inventory from "./pages/Inventory";
import AssetDetail from "./pages/AssetDetail";
import { HowItWorks, Sourcing } from "./pages/Process";
import { ReviewsPage, Contact } from "./pages/People";
import WriteReview from "./pages/WriteReview";
import { Privacy, Terms, NotFound } from "./pages/Legal";
import { AdminLogin, ForgotPassword, ResetPassword } from "./pages/admin/AuthPages";
import AdminLayout from "./pages/admin/AdminLayout";
import Overview from "./pages/admin/Overview";
import AssetsAdmin from "./pages/admin/AssetsAdmin";
import AssetEditor from "./pages/admin/AssetEditor";
import { SourcingAdmin } from "./pages/admin/RequestsAdmin";
import InquiryManager from "./pages/admin/InquiryManager";
import ReviewsAdmin from "./pages/admin/ReviewsAdmin";
import SettingsAdmin from "./pages/admin/SettingsAdmin";
import UsersAdmin from "./pages/admin/UsersAdmin";
import WeeklyReport from "./pages/admin/WeeklyReport";
import SharedPdfs from "./pages/admin/SharedPdfs";
import OpportunitiesAdmin from "./pages/admin/OpportunitiesAdmin";
import Opportunities from "./pages/Opportunities";
import { OwnerOnly } from "./pages/admin/AdminLayout";
import DivisionPage from "./pages/DivisionPage";
import { CareersPage, JobDetailPage, ApplyPage } from "./pages/Careers";
import JobsAdmin from "./pages/admin/JobsAdmin";
import ApplicationsAdmin from "./pages/admin/ApplicationsAdmin";
import JobDetailsAdmin from "./pages/admin/JobDetailsAdmin";

let lenis = null;

function PublicLayout() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let id;
    const raf = (t) => {
      lenis.raf(t);
      id = requestAnimationFrame(raf);
    };
    id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
      lenis = null;
    };
  }, []);
  useEffect(() => {
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  useEffect(() => {
    if (pathname !== "/inventory" || window.scrollY < 600) return;
    if (lenis) lenis.scrollTo(320, { duration: 0.8 });
  }, [search, pathname]);

  return (
    <>
      <Header />
      <main><Outlet /></main>
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <SettingsProvider>
      <BrowserRouter>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/divisions" element={<Divisions />} />
              <Route path="/divisions/:key" element={<DivisionPage />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/inventory/item/:id" element={<AssetDetail />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/sourcing" element={<Sourcing />} />
              <Route path="/business-opportunities" element={<Opportunities />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/careers/:slug" element={<JobDetailPage />} />
              <Route path="/careers/:slug/apply" element={<ApplyPage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/review/:token" element={<WriteReview />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route element={<AuthProvider><Outlet /></AuthProvider>}>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Overview />} />
              <Route path="inventory" element={<AssetsAdmin />} />
              <Route path="inventory/new" element={<AssetEditor />} />
              <Route path="inventory/:id" element={<AssetEditor />} />
              <Route path="inquiries" element={<InquiryManager />} />
              <Route path="sourcing" element={<SourcingAdmin />} />
              <Route path="opportunities" element={<OpportunitiesAdmin />} />
              <Route path="reviews" element={<OwnerOnly><ReviewsAdmin /></OwnerOnly>} />
              <Route path="careers" element={<OwnerOnly><JobsAdmin /></OwnerOnly>} />
              <Route path="careers/applications" element={<OwnerOnly><ApplicationsAdmin /></OwnerOnly>} />
              <Route path="careers/details" element={<OwnerOnly><JobDetailsAdmin /></OwnerOnly>} />
              <Route path="users" element={<UsersAdmin />} />
              <Route path="reports" element={<OwnerOnly><WeeklyReport /></OwnerOnly>} />
              <Route path="shared-pdfs" element={<OwnerOnly><SharedPdfs /></OwnerOnly>} />
              <Route path="settings" element={<SettingsAdmin />} />
            </Route>
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-right" theme="dark" />
    </SettingsProvider>
  );
}
